import { FluentProvider, teamsLightTheme } from "@fluentui/react-components";
import { useEffect } from "react";
import { createPortal } from "react-dom";

import { useContextMenuStore } from "../../store/context-menu.store.js";
import {
  FloatingWindowRenderer,
  getFloatingWindowPositionFromAnchor,
} from "../floating-window/floating-window-renderer.js";
import { TableContextMenu } from "./table-context-menu.jsx";

const RIGHT_MOUSE_BUTTON = 2;
const TABLE_CONTEXT_MENU_WIDTH_PX = 200;
const TABLE_CONTEXT_MENU_HEIGHT_PX = 320;

export const ContextMenuRenderer = () => {
  const { show, screenPosition, content, hideContextMenu } =
    useContextMenuStore();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Ignore right-clicks
      if (event.button === RIGHT_MOUSE_BUTTON) {
        return;
      }

      // Check if click is inside the context menu
      const target = event.target as HTMLElement;
      if (target.closest("[data-context-menu]")) {
        return;
      }

      hideContextMenu();
    };

    window.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [hideContextMenu]);

  if (!(show && screenPosition?.isVisible && content)) {
    return null;
  }

  const rootElement = document.querySelector("#root");
  if (!rootElement) {
    return null;
  }

  return createPortal(
    <FluentProvider theme={teamsLightTheme}>
      <FloatingWindowRenderer
        blocksHover={true}
        containerClassName="fixed z-50"
        contentClassName="h-full"
        isContextMenu={true}
        panelClassName="w-[200px] max-h-[calc(100vh-32px)] overflow-y-auto rounded-lg bg-white font-semibold text-xs shadow-lg"
        positionStyle={getFloatingWindowPositionFromAnchor({
          anchor: {
            x: screenPosition.x,
            y: screenPosition.y,
          },
          panelHeightPx: TABLE_CONTEXT_MENU_HEIGHT_PX,
          panelWidthPx: TABLE_CONTEXT_MENU_WIDTH_PX,
        })}
      >
        {content.type === "table" && (
          <TableContextMenu
            channelUuid={content.channelUuid}
            displayName={content.displayName}
            onNavigationCompleted={hideContextMenu}
            tableId={content.tableId}
          />
        )}
      </FloatingWindowRenderer>
    </FluentProvider>,
    rootElement
  );
};
